import { useEffect, useState } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Truck as TruckIcon, RefreshCcw } from "lucide-react";
import { jobIcon, FitBounds } from "@/components/AdminLiveMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MoverMap = () => {
  const [day, setDay] = useState("today");
  const [jobs, setJobs] = useState(null);
  const [route, setRoute] = useState(null);

  const load = (d = day) =>
    axios.get(`${API}/my/map?day=${d}`).then(({ data }) => setJobs(data)).catch(() => setJobs([]));

  useEffect(() => {
    setJobs(null);
    setRoute(null);
    load(day);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day]);

  const showRoute = (jid) =>
    axios.get(`${API}/route/${jid}`).then(({ data }) => setRoute({ job_id: jid, points: data.points || [] })).catch(() => {});

  const points = (jobs || []).map((j) => [j.pickup_geo.lat, j.pickup_geo.lng]);
  if (route?.points?.length) points.push(route.points[route.points.length - 1]);

  return (
    <div data-testid="mover-map-screen">
      <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-200 p-1">
        {[["today", "Today"], ["tomorrow", "Tomorrow"]].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setDay(k)}
            className={`rounded-lg py-3 text-xs font-bold uppercase tracking-widest transition-all ${day === k ? "bg-white text-blaze shadow" : "text-slate-500"}`}
            data-testid={`mover-map-${k}`}
          >
            {l}
          </button>
        ))}
      </div>

      {jobs === null ? (
        <div className="flex justify-center py-16"><RefreshCcw className="h-5 w-5 animate-spin text-blaze" /></div>
      ) : jobs.length === 0 ? (
        <div className="mt-4 rounded-2xl border border-dashed border-line bg-white/70 p-10 text-center" data-testid="mover-map-empty">
          <MapPin className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3 text-sm font-semibold text-slate-600">No jobs assigned for {day}.</p>
        </div>
      ) : (
        <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.06)]" data-testid="mover-live-map">
          <p className="px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">
            Tap a job ID to see full details &amp; the route in blue
          </p>
          <div className="relative z-0 h-[calc(100dvh-320px)] min-h-[360px]">
            <MapContainer center={[-37.8136, 144.9631]} zoom={10} style={{ height: "100%", width: "100%" }} scrollWheelZoom={true}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitBounds points={points} />
              {jobs.map((j) => (
                <Marker
                  key={j.job_id}
                  position={[j.pickup_geo.lat, j.pickup_geo.lng]}
                  icon={jobIcon(j.job_id, { done: j.status === "completed", pulse: j.status !== "completed" })}
                  eventHandlers={{ click: () => showRoute(j.job_id) }}
                >
                  <Popup minWidth={230}>
                    <div data-testid={`mover-map-popup-${j.job_id}`}>
                      <p className="m-0 font-mono text-sm font-bold text-blue-600">{j.job_id}</p>
                      <p className="m-0 mt-1 text-xs font-semibold text-slate-700">{j.moving_date} · {j.start_time}</p>
                      <p className="m-0 mt-2 text-xs"><strong className="text-green-700">Pickup:</strong> {j.pickup}</p>
                      <p className="m-0 text-xs"><strong className="text-red-600">Drop-off:</strong> {j.drop}</p>
                      <p className="m-0 mt-1 flex items-center gap-1 text-xs text-slate-600"><TruckIcon className="h-3 w-3" /> {j.truck}</p>
                      <p className="m-0 mt-2 text-[10px] font-bold uppercase tracking-wider text-blue-600">Route shown in blue on the map</p>
                    </div>
                  </Popup>
                </Marker>
              ))}
              {route && route.points.length > 0 && (
                <>
                  <Polyline positions={route.points} pathOptions={{ color: "#2563eb", weight: 5, opacity: 0.85 }} />
                  <Marker position={route.points[route.points.length - 1]} icon={jobIcon("DROP-OFF", { color: "#dc2626" })}>
                    <Popup><p className="m-0 text-xs font-bold text-slate-800">Drop-off — {route.job_id}</p></Popup>
                  </Marker>
                </>
              )}
            </MapContainer>
          </div>
        </div>
      )}
    </div>
  );
};
